import { AgentProgressStep } from '@/agent-v2/components/AgentProgressStep'
import type { AgentV2WorkLogItem } from '@/agent-v2/store/reducer'
import { BrandedLoader } from '@/components/animations/BrandedLoader'
import { ChevronRight } from '@/assets/icons'

export function AgentV2RunProgress({
  running,
  workLogItems,
  compact = false,
  durationSeconds,
}: {
  running: boolean
  workLogItems: AgentV2WorkLogItem[]
  compact?: boolean
  durationSeconds?: number
}) {
  const showThinking = running && (workLogItems.length === 0 || workLogItems.some((item) => item.status === 'active'))
  const collapsible = compact || !running
  const durationLabel =
    durationSeconds === undefined ? undefined : durationSeconds >= 60 ? `${Math.floor(durationSeconds / 60)}m ${durationSeconds % 60}s` : `${durationSeconds}s`
  const workLog = (
    <div aria-label="Work log" role="log">
      {showThinking && (
        <div className="flex items-center gap-1">
          <BrandedLoader size={20} />
          <span className="animate-shimmer-primary text-sm font-medium leading-6 tracking-[-0.32px]">Thinking...</span>
        </div>
      )}
      {(collapsible || showThinking) && workLogItems.length > 0 && (
        <div aria-hidden className="flex h-5 w-5 flex-col items-center py-1">
          <div className="w-px flex-1 bg-brand-neutral-300" />
        </div>
      )}
      {workLogItems.map((item, index) => (
        <div key={item.id} className="relative pb-5 last:pb-0">
          <AgentProgressStep label={item.label} status={item.status} />
          {index < workLogItems.length - 1 && <div aria-hidden className="absolute bottom-1 left-2.5 top-7 w-px -translate-x-1/2 bg-brand-neutral-300" />}
        </div>
      ))}
    </div>
  )

  if (collapsible) {
    return (
      <details className="agent-v2-compact-progress group mb-4" key={running ? 'running' : 'finished'}>
        <summary className="flex w-fit cursor-pointer list-none items-center gap-1 text-sm font-medium leading-6 tracking-[-0.32px] text-brand-neutral-600 [&::-webkit-details-marker]:hidden">
          <span className={showThinking ? 'animate-shimmer-neutral' : undefined}>
            {running ? workLogItems.at(-1)?.label ?? 'Thinking…' : durationLabel === undefined ? 'Reasoned' : `Reasoned for ${durationLabel}`}
          </span>
          <ChevronRight aria-hidden className="size-4 shrink-0 transition-transform group-open:rotate-90" />
        </summary>
        {workLog}
      </details>
    )
  }

  return <div className="mb-3 pb-2">{workLog}</div>
}
